import { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, Calendar as CalendarIcon, Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const HeuresStage = ({ user }) => {
  const [heures, setHeures] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [heuresJour, setHeuresJour] = useState('');
  const [activite, setActivite] = useState('Présence en structure');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    loadHeures();
  }, []);

  const loadHeures = async () => {
    try {
      const response = await axios.get(`/heures-stage/stagiaire/${user.user_id}`);
      setHeures(response.data.heures || []);
      setTotal(response.data.total || 0);
    } catch (error) {
    }
  };

  const handleAddHeures = async (e) => {
    e.preventDefault();
    
    if (!heuresJour || parseFloat(heuresJour) <= 0) {
      toast.error('Veuillez entrer un nombre d\'heures valide');
      return;
    }

    try {
      await axios.post('/heures-stage', {
        date: selectedDate,
        heures: parseFloat(heuresJour),
        activite: activite || 'Présence en structure'
      });
      
      toast.success('Heures enregistrées');
      setHeuresJour('');
      loadHeures();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteHeure = async (heureId) => {
    try {
      await axios.delete(`/heures-stage/${heureId}`);
      toast.success('Entrée supprimée');
      loadHeures();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < (startingDay === 0 ? 6 : startingDay - 1); i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getHeuresForDate = (day) => {
    if (!day) return null;
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const heuresList = heures.filter(h => h.date === dateStr);
    if (heuresList.length === 0) return null;
    return heuresList.reduce((sum, h) => sum + (h.heures || 0), 0);
  };

  const handleDateClick = (day) => {
    if (!day) return;
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <div className="space-y-6" data-testid="heures-stage-page">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Mes Heures de Stage</h1>
        <p className="text-slate-600">Saisissez vos heures de présence quotidiennes sur votre structure</p>
      </div>

      {/* Compteur Total */}
      <div className="bg-gradient-to-r from-[#8DC63F] to-[#7db336] rounded-lg p-8 shadow-lg text-white" data-testid="total-heures-card">
        <div className="flex items-center space-x-6">
          <div className="bg-white/20 p-4 rounded-lg">
            <Clock className="h-12 w-12" />
          </div>
          <div>
            <p className="text-5xl font-display font-bold mb-2">{total}h</p>
            <p className="text-lg opacity-90">Total heures de stage</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendrier */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-display font-semibold text-[#0F172A] flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2 text-[#8DC63F]" />
              Calendrier
            </h2>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="font-medium text-[#0F172A] min-w-[140px] text-center">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-1">
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                {day}
              </div>
            ))}
            {getDaysInMonth(currentMonth).map((day, idx) => {
              const heuresDay = getHeuresForDate(day);
              const isSelected = day && selectedDate === `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isToday = day && new Date().toISOString().split('T')[0] === `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              
              return (
                <div
                  key={idx}
                  onClick={() => handleDateClick(day)}
                  className={`
                    relative h-12 flex flex-col items-center justify-center rounded-lg text-sm cursor-pointer transition-all
                    ${!day ? 'bg-transparent' : 'hover:bg-slate-100'}
                    ${isSelected ? 'bg-[#8DC63F] text-white hover:bg-[#8DC63F]' : ''}
                    ${isToday && !isSelected ? 'border-2 border-[#8DC63F]' : ''}
                  `}
                  data-testid={day ? `calendar-day-${day}` : undefined}
                >
                  {day && (
                    <>
                      <span className={`font-medium ${isSelected ? 'text-white' : 'text-[#0F172A]'}`}>{day}</span>
                      {heuresDay && (
                        <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-[#8DC63F]'}`}>
                          {heuresDay}h
                        </span>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Formulaire de saisie */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center">
            <Plus className="h-5 w-5 mr-2 text-[#8DC63F]" />
            Ajouter des heures
          </h2>
          
          <form onSubmit={handleAddHeures} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                data-testid="input-date"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="heures">Nombre d'heures</Label>
              <Input
                id="heures"
                type="number"
                step="0.5"
                min="0.5"
                max="12"
                placeholder="Ex: 7"
                value={heuresJour}
                onChange={(e) => setHeuresJour(e.target.value)}
                data-testid="input-heures"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="activite">Activité (optionnel)</Label>
              <Input
                id="activite"
                type="text"
                placeholder="Présence en structure"
                value={activite}
                onChange={(e) => setActivite(e.target.value)}
                data-testid="input-activite"
              />
            </div>

            <Button type="submit" className="w-full bg-[#8DC63F] hover:bg-[#7db336]" data-testid="submit-heures">
              <Plus className="h-4 w-4 mr-2" />
              Enregistrer les heures
            </Button>
          </form>
        </div>
      </div>

      {/* Liste des heures récentes */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Historique des heures</h2>
        <div className="space-y-3">
          {heures.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Aucune heure enregistrée</p>
          ) : (
            heures.slice(0, 20).map((heure) => (
              <div
                key={heure.heure_id}
                className="p-4 border border-slate-200 rounded-lg hover:border-[#8DC63F] transition-colors flex items-center justify-between"
                data-testid={`heure-item-${heure.heure_id}`}
              >
                <div className="flex items-center space-x-4">
                  <div className="bg-[#8DC63F]/10 p-3 rounded-lg">
                    <CalendarIcon className="h-5 w-5 text-[#8DC63F]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A]">{heure.activite || 'Présence en structure'}</p>
                    <p className="text-sm text-slate-600">
                      {new Date(heure.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <p className="text-2xl font-display font-bold text-[#8DC63F]">{heure.heures}h</p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDeleteHeure(heure.heure_id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-heure-${heure.heure_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HeuresStage;
